import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Form, Control, Errors } from 'react-redux-form';
import InputFields from '../InputFields';
import { useDispatch, useSelector } from 'react-redux';
import { REFRESH_USER } from '../../store/constants';
import Logo from '../../global/img/logo_full_white.png';
import { connect } from 'react-redux';
import AlertMessage from '../AlertMessage';
import Loading from '../Loading';
import './EmailVerificationModal.css';

const EmailVerificationModal = ({ show, onClose, userEmail, returnUrl }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const isEmailVerified = Boolean(user?.emailVerified || Number(user?.email_verified));
    const [email, setEmail] = useState(userEmail || '');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });

    const handleResendVerification = async (e) => {
        e.preventDefault();
        
        if (!email) {
            setError('Email address is required');
            return;
        }

        setLoading(true);
        setError('');
        setAlert({ show: false, type: '', message: '' });

        try {
            const response = await fetch('/resend-email-verification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (response.ok) {
                setSent(true);
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Verification email sent successfully! Please check your inbox.'
                });
            } else {
                setError(data.message || 'Failed to send verification email');
                setAlert({
                    show: true,
                    type: 'danger',
                    message: data.message || 'Failed to send verification email'
                });
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            setAlert({
                show: true,
                type: 'danger',
                message: 'Network error. Please try again later.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        // Clear session and redirect to login
        fetch('/api/api/logout', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(() => {
            window.location.href = '/login';
        }).catch(() => {
            window.location.href = '/login';
        });
    };

    useEffect(() => {
        if (!show) return;

        // If verified, close and send user back to the requested return URL.
        if (isEmailVerified) {
            setSent(false);
            setError('');
            setAlert({ show: false, type: '', message: '' });
            if (typeof onClose === 'function') {
                onClose();
            }
            const safeReturn = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/';
            window.location.href = safeReturn;
            return;
        }

        const refreshUserStatus = () => {
            if (!user?.id || !user?.token) return;
            dispatch({ type: REFRESH_USER });
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshUserStatus();
            }
        };

        refreshUserStatus();
        const intervalId = setInterval(refreshUserStatus, 15000);
        window.addEventListener('focus', refreshUserStatus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('focus', refreshUserStatus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [dispatch, isEmailVerified, onClose, returnUrl, show, user?.id, user?.token]);

    if (!show) return null;

    return (
        <Modal show={show} onHide={onClose} centered size='lg'>
            <Modal.Body onClick={(e) => e.stopPropagation()}>
                <section className='signupMainSection'>
                    <div className='signupMainDiv'>
                        <div className='text-center'>
                            <img src={Logo} alt='Inside Injuries Logo' className='signupLogo' />
                        </div>
                        <div className='signupFormDiv'>
                            {!sent ? (
                                <>
                                    <div className='text-center mb-4'>
                                        <h4 className='verificationTitle'>Email Verification Required</h4>
                                        <p className='verificationSubtitle'>
                                            Please verify your email address to access this content.
                                        </p>
                                        {userEmail ? (
                                            <div className='userEmailSection'>
                                                <p className='userEmail'>
                                                    <strong>Account email:</strong> {userEmail}
                                                </p>
                                                <p className='emailNote'>
                                                    We'll send the verification email to this address.
                                                </p>
                                            </div>
                                        ) : (
                                            <p className='emailNote'>
                                                Enter your email address to receive a verification link.
                                            </p>
                                        )}
                                    </div>

                                    {alert.show && (
                                        <AlertMessage 
                                            type={alert.type} 
                                            message={alert.message}
                                            onClose={() => setAlert({ show: false, type: '', message: '' })}
                                        />
                                    )}

                                    {!userEmail ? (
                                        <Form model="emailVerification" onSubmit={handleResendVerification}>
                                            <div className="inputCol">
                                                <InputFields 
                                                    model=".email" 
                                                    inputType="text" 
                                                    type="email" 
                                                    placeholder="Enter your email" 
                                                    label="Email Address"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                />
                                            </div>

                                            {error && (
                                                <div className="formInputError text-center mb-3">
                                                    {error}
                                                </div>
                                            )}

                                                <Control.button 
                                                    model="emailVerification" 
                                                    className={`loginButton ${loading ? 'loading' : ''}`} 
                                                    type="submit"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <span className="button-loader">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="spinner">
                                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25"/>
                                                                <path d="M12 2a10 10 0 0 1 0 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                                                    <animateTransform
                                                                        attributeName="transform"
                                                                        type="rotate"
                                                                        from="0 12 12"
                                                                        to="360 12 12"
                                                                        dur="0.8s"
                                                                        repeatCount="indefinite"
                                                                    />
                                                                </path>
                                                            </svg>
                                                            Sending...
                                                        </span>
                                                    ) : 'SEND VERIFICATION EMAIL'}
                                                </Control.button>
                                        </Form>
                                    ) : (
                                        <div>
                                            {error && (
                                                <div className="formInputError text-center mb-3">
                                                    {error}
                                                </div>
                                            )}

                                            <div className="w-100">
                                                <button 
                                                    className={`loginButton ${loading ? 'loading' : ''}`} 
                                                    onClick={handleResendVerification}
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <span className="button-loader">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="spinner">
                                                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25"/>
                                                                <path d="M12 2a10 10 0 0 1 0 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                                                    <animateTransform
                                                                        attributeName="transform"
                                                                        type="rotate"
                                                                        from="0 12 12"
                                                                        to="360 12 12"
                                                                        dur="0.8s"
                                                                        repeatCount="indefinite"
                                                                    />
                                                                </path>
                                                            </svg>
                                                            Sending...
                                                        </span>
                                                    ) : 'SEND VERIFICATION EMAIL'}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="text-center mt-3">
                                        <button 
                                            className="btn btn-link text-muted p-0"
                                            onClick={handleLogout}
                                        >
                                            Logout
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className='text-center'>
                                    <div className='successIcon mb-4'>
                                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" stroke="#28a745" strokeWidth="2"/>
                                            <path d="M8 12l2 2 4-4" stroke="#28a745" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    </div>
                                    
                                    <h4 className='verificationTitle'>Verification Email Sent!</h4>
                                    <p className='verificationSubtitle mb-4'>
                                        We've sent a verification email to <strong>{email}</strong>.
                                    </p>
                                    <p className='verificationNote mb-4'>
                                        Please check your inbox and click the verification link to continue.
                                        Don't forget to check your spam folder!
                                    </p>
                                    
                                    <div className="w-100">
                                        <button 
                                            className="loginButton"
                                            onClick={() => window.open('https://mail.google.com', '_blank')}
                                        >
                                            OPEN EMAIL
                                        </button>
                                    </div>
                                    
                                    <div className="text-center mt-3">
                                        <button 
                                            className="btn btn-link text-muted p-0 me-3"
                                            onClick={() => setSent(false)}
                                        >
                                            Resend Email
                                        </button>
                                        <button 
                                            className="btn btn-link text-muted p-0"
                                            onClick={handleLogout}
                                        >
                                            Logout
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </Modal.Body>
        </Modal>
    );
};

export default connect()(EmailVerificationModal);